import { Dispatch, FC, memo, ReactNode, SetStateAction, useCallback, useMemo, useState } from "react";
import { Metric } from "@models/Metric";
import { IOption } from "@models";
import { SelectSingle } from "@components/Select";
import SelectFilters from "@vms/SelectFilters";
import { SELECT_FILTERS_AS } from "@vms/SelectFilters/SelectFilters";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import { FiSearch } from "react-icons/fi";
import TextInput from "@components/TextInput";
import { Tooltip } from "@datassential/platform-ui-lib";

const CorrelationSubFilters: FC<{
	selectedCorrelatingMetric: Metric | null,
	setSelectedCorrelatingMetric: Dispatch<SetStateAction<Metric | null>>,
	metricOptions: IOption<Metric, string>[],
	selectedHeadline: ReactNode,
	search: string,
	setSearch: Dispatch<SetStateAction<string>>,
}> = ({
	selectedCorrelatingMetric,
	setSelectedCorrelatingMetric,
	metricOptions,
	selectedHeadline,
	search,
	setSearch,
}) => {
	const [isSelecting, setIsSelecting] = useState(false)

	const {
		appliedFilters,
	} = useSelectFilters()

	const filtersPlaceholder = useMemo(() => {
		const titles = []
		if (appliedFilters.macStages.length) {
			titles.push(`${appliedFilters.macStages.length} MAC Stage${appliedFilters.macStages.length > 1 ? "s" : ""}`)
		}
		if (appliedFilters.heatScores.length) {
			titles.push(`${appliedFilters.heatScores.length} Heat Score${appliedFilters.heatScores.length > 1 ? "s" : ""}`)
		}

		if (appliedFilters.categories.length) {
			titles.push(`${appliedFilters.categories.length} ${appliedFilters.categories.length > 1 ? "Categories" : "Category"}`)
		}

		return titles.length ? titles.join(" & ") : "Foods & Flavors"
	}, [appliedFilters])

	const onSelectOpenChange = useCallback((isOpened: boolean) => setIsSelecting(isOpened), [])

	const showTooltip = useMemo(() => !isSelecting && !selectedCorrelatingMetric, [isSelecting, selectedCorrelatingMetric])

	return (
		<div className="CorrelationSubFilters appears-top">
			<div className="CorrelationSubFilters__container">
				<div
					className="CorrelationSubFilters__headline"
					data-testid="blockCorrelationSubFiltersHeadline"
				>
					THOSE WHO
					<span
						className="CorrelationSubFilters__selected-text"
						data-testid="textCorrelationSelectedHeadline"
					>{selectedHeadline}</span>
					<span data-testid="textCorrelationRelatingMetricLabel">ALSO:</span>
					<div data-tooltip-id="correlation-select-relating-metric">
						<SelectSingle
							className="CorrelationSubFilters__select-metric"
							placeholder="-"
							value={selectedCorrelatingMetric}
							setValue={setSelectedCorrelatingMetric}
							options={metricOptions}
							modifiers={!selectedCorrelatingMetric ? ["highlighted"] : []}
							onOpenChange={onSelectOpenChange}
							testId="selectCorrelationRelatingMetric"
						/>
					</div>
					<Tooltip
						id="correlation-select-relating-metric"
						className="CorrelationSubFilters__tooltip levitate"
						isOpen={showTooltip}
					>
						<span data-testid="textCorrelationRelatingMetricTooltip">Now select a metric for comparison</span>
					</Tooltip>
					<SelectFilters
						className="CorrelationSubFilters__search-filters"
						testId="buttonCorrelationFilters"
						label="THESE:"
						placeholder={filtersPlaceholder}
						as={SELECT_FILTERS_AS.FIELD}
					/>
				</div>
				{
					selectedCorrelatingMetric ? (
						<TextInput
							className="CorrelationSubFilters__search appears-left"
							placeholder="filter results"
							testId="blockCorrelationSearchFilter"
							prefix={<FiSearch/>}
							value={search}
							setValue={setSearch}
						/>
					) : null
				}
			</div>
		</div>
	)
}

export default memo(CorrelationSubFilters);